import { useCallback, useEffect, useRef, useState } from 'react';

const resolveUrl = (token) => {
  const explicit = (process.env.REACT_APP_WS_URL || '').trim().replace(/\/$/, '');
  if (explicit) return `${explicit}/ws/client-messaging/?token=${encodeURIComponent(token)}`;
  const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL_DEV || process.env.REACT_APP_API_URL_PROD;
  if (apiUrl && /^https?:\/\//i.test(apiUrl)) {
    const base = apiUrl.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:').replace(/\/api\/?$/i, '').replace(/\/$/, '');
    return `${base}/ws/client-messaging/?token=${encodeURIComponent(token)}`;
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const local = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const host = local ? `${window.location.hostname}:8000` : window.location.host;
  return `${protocol}//${host}/ws/client-messaging/?token=${encodeURIComponent(token)}`;
};

export const useClientMessagingWebSocket = ({ enabled = true, token, onMessage } = {}) => {
  const [status, setStatus] = useState('idle');
  const socketRef = useRef(null);
  const reconnectRef = useRef(null);
  const heartbeatRef = useRef(null);
  const messageHandlerRef = useRef(onMessage);
  messageHandlerRef.current = onMessage;

  const cleanup = useCallback(() => {
    window.clearTimeout(reconnectRef.current);
    window.clearInterval(heartbeatRef.current);
    if (socketRef.current) {
      socketRef.current.onclose = null;
      socketRef.current.close();
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    let active = true;
    if (!enabled || !token) return undefined;
    const connect = () => {
      if (!active) return;
      setStatus('connecting');
      const socket = new WebSocket(resolveUrl(token));
      socketRef.current = socket;
      socket.onopen = () => {
        setStatus('open');
        heartbeatRef.current = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ action: 'heartbeat' }));
          }
        }, 30000);
      };
      socket.onmessage = (event) => {
        try {
          messageHandlerRef.current?.(JSON.parse(event.data));
        } catch (_error) {
          // Ignore malformed transport frames.
        }
      };
      socket.onerror = () => setStatus('error');
      socket.onclose = () => {
        setStatus('closed');
        window.clearInterval(heartbeatRef.current);
        if (active) reconnectRef.current = window.setTimeout(connect, 1500);
      };
    };
    connect();
    return () => {
      active = false;
      cleanup();
    };
  }, [cleanup, enabled, token]);

  const sendJson = useCallback((payload) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(payload));
    return true;
  }, []);

  return { status, sendJson };
};
